/** Дрібні перевикористовувані елементи інтерфейсу. */

import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Loader2, X } from 'lucide-react'

// ── Modal ────────────────────────────────────────────────────────────────────

/**
 * Модальне вікно. Клавіатура: Escape закриває, Tab не виходить за межі вікна,
 * фон кликом закриває, а скролл сторінки блокується.
 */
export function Modal({ open, onClose, title, subtitle, children, footer, size = 'md', closeOnBackdrop = true, className = '' }) {
  const panelRef = useRef(null)

  useEffect(() => {
    if (!open) return

    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose?.()
        return
      }
      if (e.key !== 'Tab' || !panelRef.current) return

      const focusables = panelRef.current.querySelectorAll(
        'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])',
      )
      if (!focusables.length) return
      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }

    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', onKey, true)

    // Фокус на перший елемент — щоб клавіатура одразу працювала
    const t = setTimeout(() => {
      const target = panelRef.current?.querySelector('input, textarea, button, [contenteditable]')
      target?.focus?.()
    }, 40)

    return () => {
      document.body.style.overflow = prevOverflow
      document.removeEventListener('keydown', onKey, true)
      clearTimeout(t)
    }
  }, [open, onClose])

  if (!open) return null

  const widths = {
    sm: 'max-w-md',
    md: 'max-w-2xl',
    lg: 'max-w-4xl',
    xl: 'max-w-6xl',
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={closeOnBackdrop ? onClose : undefined}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        className={`app-modal animate-card-in relative flex max-h-[92vh] w-full flex-col overflow-hidden ${className}
                    rounded-t-2xl border border-slate-200 bg-white shadow-2xl sm:rounded-2xl
                    dark:border-slate-800 dark:bg-slate-900 ${widths[size] ?? widths.md}`}
      >
        {(title || onClose) && (
          <header className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4 dark:border-slate-800">
            <div className="min-w-0">
              {title && <h2 className="truncate text-lg font-bold">{title}</h2>}
              {subtitle && <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">{subtitle}</p>}
            </div>
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                className="btn-ghost -mt-1 -mr-2 shrink-0 !px-2 !py-1"
                aria-label="Закрити"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </header>
        )}

        <div className="scroll-y flex-1 px-5 py-4">{children}</div>

        {footer && (
          <footer className="safe-bottom flex flex-wrap items-center justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3 dark:border-slate-800 dark:bg-slate-950/60">
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body,
  )
}

// ── Field ────────────────────────────────────────────────────────────────────

export function Field({ label, hint, error, children, required, className = '' }) {
  return (
    <label className={`block ${className}`}>
      {label && (
        <span className="label">
          {label}
          {required && <span className="ml-0.5 text-rose-500">*</span>}
        </span>
      )}
      {children}
      {hint && !error && <span className="mt-1 block text-xs text-slate-500 dark:text-slate-400">{hint}</span>}
      {error && <span className="mt-1 block text-xs font-medium text-rose-600 dark:text-rose-400">{error}</span>}
    </label>
  )
}

// ── Spinner ──────────────────────────────────────────────────────────────────

export function Spinner({ className = 'h-5 w-5' }) {
  return <Loader2 className={`${className} animate-spin`} />
}

export function Loading({ label = 'Завантаження…', className = '' }) {
  return (
    <div className={`loading-stage ${className}`}>
      <div className="loading-skeleton h-2 w-32" />
      <span>{label}</span>
    </div>
  )
}

// ── Badge / Tag ──────────────────────────────────────────────────────────────

export function Badge({ children, className = '', title }) {
  return (
    <span title={title} className={`chip ${className}`}>
      {children}
    </span>
  )
}

export function TagPill({ name, active, count, onClick, onRemove }) {
  return (
    <span
      className={`chip ${active ? 'chip-active' : ''} group ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={(e) => {
        if (onClick && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault()
          onClick()
        }
      }}
    >
      <span className="text-slate-400 dark:text-slate-500">#</span>
      <span className="truncate">{name}</span>
      {count !== undefined && (
        <span className="opacity-60 tabular-nums">{count}</span>
      )}
      {onRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onRemove(name)
          }}
          className="-mr-1 ml-0.5 opacity-0 transition group-hover:opacity-70 hover:opacity-100"
          aria-label={`Прибрати тег ${name}`}
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </span>
  )
}

// ── Alert ────────────────────────────────────────────────────────────────────

export function Alert({ tone = 'info', title, children, className = '' }) {
  const tones = {
    info: 'border-sky-300 bg-sky-50 text-sky-900 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-100',
    warn: 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100',
    error: 'border-rose-300 bg-rose-50 text-rose-900 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-100',
    ok: 'border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-100',
  }
  return (
    <div className={`rounded-xl border px-4 py-3 text-sm ${tones[tone] ?? tones.info} ${className}`} role={tone === 'error' ? 'alert' : undefined}>
      {title && <p className="font-semibold">{title}</p>}
      {children && <div className={title ? 'mt-1' : ''}>{children}</div>}
    </div>
  )
}

// ── Segmented ────────────────────────────────────────────────────────────────

export function Segmented({ options, value, onChange, className = '' }) {
  return (
    <div className={`segmented inline-flex rounded-xl border border-slate-200 bg-slate-100 p-1 dark:border-slate-800 dark:bg-slate-900 ${className}`}>
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition ${
            value === opt.value
              ? 'is-active bg-white text-slate-900 shadow-sm dark:bg-slate-800 dark:text-white'
              : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'
          }`}
          aria-pressed={value === opt.value}
        >
          {opt.icon}
          {opt.label}
        </button>
      ))}
    </div>
  )
}

// ── Progress ─────────────────────────────────────────────────────────────────

export function Progress({ value, max = 100, className = '', barClass = 'bg-brand-500' }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0
  return (
    <div className={`h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800 ${className}`}>
      <div className={`h-full rounded-full transition-[width] duration-300 ${barClass}`} style={{ width: `${pct}%` }} />
    </div>
  )
}

// ── Empty ────────────────────────────────────────────────────────────────────

export function Empty({ icon: Icon, title, children, action }) {
  return (
    <div className="empty-stage flex flex-col items-center justify-center gap-3 px-6 text-center">
      {Icon && (
        <div className="rounded-2xl bg-slate-100 p-4 text-slate-400 dark:bg-slate-800 dark:text-slate-500">
          <Icon className="h-8 w-8" />
        </div>
      )}
      <p className="text-base font-semibold">{title}</p>
      {children && <div className="max-w-md text-sm text-slate-500 dark:text-slate-400">{children}</div>}
      {action}
    </div>
  )
}
